import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { useFacilityQuery } from "Api/Queries/Merchants/useFacilityQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { InfoGroup } from "Components/Contact/InfoGroup";
import { DataRow } from "Components/Shared/DataRow/DataRow";
import { MapEmbed } from "Components/Shared/Components/MapEmbed";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useParams } from "react-router-dom";
import { Resources, useResource } from "Translations/Resources";
import { getFormattedAddress } from "Utils/AddressUtils";
import { appUrl } from "Utils/UrlUtils";
import { dtoToState, formatDayHours } from "Utils/OpeningHoursUtils";
import { useWeekDayOptions } from "Hooks/Options/useWeekDayOptions";
import { formatPhone, isPhoneEmpty, phoneToE164 } from "Utils/PhoneUtils";
import type { ReactNode } from "react";
import type { MerchantFacilityHoursDto } from "Infrastructure/Api/Api";

const RESOURCES = Resources.Pages.Content.WhereToPay.StoreDetail;

export const StoreDetailPage = () => {
  const { t } = useResource();
  const { id } = useParams<{ id: string }>();
  const { data: facility, isLoading, isError } = useFacilityQuery(id);
  const daysOfWeek = useWeekDayOptions();

  if (isLoading) {
    return (
      <Layout title={t(RESOURCES.NotFound)}>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (isError || !facility) {
    return (
      <Layout title={t(RESOURCES.NotFound)}>
        <Typography>{t(RESOURCES.NotFound)}</Typography>
      </Layout>
    );
  }

  const { address, name, phone, email, description, hours } = facility;
  const formattedAddress = address ? getFormattedAddress(address) : "";
  const hoursState = dtoToState(hours as MerchantFacilityHoursDto[]);
  const hasOpeningHours = hours && hours.length > 0;

  return (
    <Layout title={name}>
      <Stack gap={4}>
        <InfoGroup title={t(RESOURCES.AboutUs.Title)}>
          <Stack gap={3}>
            {description && <Typography>{description}</Typography>}
            <Stack gap={2}>
              {address && (
                <DataRow
                  label={t(RESOURCES.AboutUs.Address)}
                  value={
                    <Box display="flex" gap={1} alignItems="center">
                      <LocationOnIcon sx={{ color: "primary.main" }} />
                      <Link
                        href={`https://maps.google.com/?q=${encodeURIComponent(formattedAddress)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontWeight={500}
                        sx={{
                          textDecoration: "inherit",
                          color: "primary.main",
                        }}
                      >
                        {formattedAddress}
                      </Link>
                    </Box>
                  }
                />
              )}
              {!isPhoneEmpty(phone) && (
                <DataRow
                  label={t(RESOURCES.AboutUs.Phone)}
                  value={
                    <Link
                      href={`tel:${phoneToE164(phone)}`}
                      sx={{ textDecoration: "inherit", fontWeight: 500 }}
                    >
                      {formatPhone(phone)}
                    </Link>
                  }
                />
              )}
              {email && (
                <DataRow
                  label={t(RESOURCES.AboutUs.Email)}
                  value={
                    <Link
                      href={`mailto:${email}`}
                      sx={{ textDecoration: "inherit", fontWeight: 500 }}
                    >
                      {email}
                    </Link>
                  }
                />
              )}
            </Stack>
          </Stack>
        </InfoGroup>
        {hasOpeningHours && (
          <InfoGroup title={t(RESOURCES.OpeningHours.Title)}>
            <Stack gap={0.5}>
              {daysOfWeek.map((day) => (
                <DataRow
                  key={day.id}
                  label={day.name}
                  value={formatDayHours(hoursState[day.id], t)}
                />
              ))}
            </Stack>
          </InfoGroup>
        )}
        {address && (
          <Box mt={2}>
            <MapEmbed address={formattedAddress} />
          </Box>
        )}
      </Stack>
    </Layout>
  );
};

type LayoutProps = {
  title: string;
  children: ReactNode;
};

const Layout = ({ title, children }: LayoutProps) => (
  <AuthenticatedLayout
    title={title}
    backNavigation={{
      mode: "historyStack",
      fallbackUrl: appUrl("where-to-pay"),
    }}
  >
    <StyledPageWrapper>
      <div className="left-content">
        <PrimaryPaper>{children}</PrimaryPaper>
      </div>
    </StyledPageWrapper>
  </AuthenticatedLayout>
);
